import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsBoxModule, DsButtonModule, DsFormFieldModule, DsRadioGroupModule } from '@bmw-ds/components';

import { CustomerAddress } from '../../../customer/model/customer-information.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { deepEqual } from '../../../utils/objectComparison';
import { CustomerComparisonField } from '../../model/customer-comparison-field.model';

@Component({
  selector: 'cdf-customer-comparison-row',
  imports: [DsBoxModule, DsRadioGroupModule, DsFormFieldModule, FormsModule, DsButtonModule, DsRadioGroupModule],
  template: `
    @if (!areValuesEqual()) {
      <div class="row">
        <span class="col-sm-4">{{ comparisonFieldLabel() }}</span>
        <div class="col-sm-8">
          <ds-form-field>
            <div ds-radio-group variant="inline" class="radio-group">
              <input
                [class.hidden]="!retailLabel()"
                ds-input
                type="radio"
                [id]="'retail-input-' + comparisonFieldType()"
                [name]="'radio-group-' + comparisonFieldType()"
                [value]="retailValue() ?? ''"
                [(ngModel)]="comparisonFieldValue"
              />
              <label
                ds-label
                [class.hidden]="!retailLabel()"
                [id]="'retail-label-' + comparisonFieldType()"
                [for]="'retail-input-' + comparisonFieldType()"
              >
                {{ retailLabel() }}
              </label>
              <input
                [class.hidden]="!nscLabel()"
                ds-input
                type="radio"
                [id]="'nsc-input-' + comparisonFieldType()"
                [name]="'radio-group-' + comparisonFieldType()"
                [value]="nscValue() ?? ''"
                [(ngModel)]="comparisonFieldValue"
              />
              <label
                ds-label
                [class.hidden]="!nscLabel()"
                [id]="'nsc-label-' + comparisonFieldType()"
                [for]="'nsc-input-' + comparisonFieldType()"
              >
                {{ nscLabel() }}
              </label>

              <!-- If current comparison field is not mandatory, allow setting value to undefined -->
              @if (!mandatoryField()) {
                <input
                  ds-input
                  type="radio"
                  [id]="'undefined-input-' + comparisonFieldType()"
                  [name]="'radio-group-' + comparisonFieldType()"
                  [value]="undefined"
                  [(ngModel)]="comparisonFieldValue"
                />
                <label
                  ds-label
                  [id]="'undefined-label-' + comparisonFieldType()"
                  [for]="'undefined-input-' + comparisonFieldType()"
                >
                  {{ i18n('nscSync.customerComparison.selectNeitherOptionLabel') }}
                </label>
              }
            </div>
          </ds-form-field>
        </div>
      </div>
    }
  `,
  styleUrl: './customer-comparison-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerComparisonRowComponent {
  protected readonly i18n = useI18n();

  readonly nscLabel = input.required<string | undefined>();
  readonly nscValue = input.required<string | boolean | CustomerAddress | undefined>();
  readonly retailLabel = input.required<string | undefined>();
  readonly retailValue = input.required<string | boolean | CustomerAddress | undefined>();

  readonly comparisonFieldValue = model.required<string | boolean | CustomerAddress | undefined>();
  readonly comparisonFieldLabel = input.required<string>();

  readonly comparisonFieldType = input.required<CustomerComparisonField>();

  /**
   * If true, the selection option to neither choose one of the values from NSC Database and Retail is removed
   */
  readonly mandatoryField = input(false);

  protected readonly areValuesEqual = computed(() => deepEqual(this.nscValue(), this.retailValue()));
}
